import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { Configuration } from '@/api/generated/MNT/configuration';
import { interceptBackgroundTask } from '@/api/interceptors/backgroundTask';
import { ApiRegion, config } from '@/config';

import { RegionalApiError } from './regionalApi';
import { clearRegionalAuthError, reportRegionalAuthError } from './regionalAuthErrors';
import { type RegionalRequestOpts, regionalRequest } from './regionalFetch';
import { getUsStaffAccessToken, invalidateStaffRegionAccessToken } from './staffRegionAuth';

type RetriableConfig = AxiosRequestConfig & { _usStaffTokenRetried?: boolean };

/**
 * Isolated axios for a non-Canada region. It must not be the shared `client`:
 * that instance logs the Canada session out on every 401.
 */
const createRegionalAxios = (region: ApiRegion, canadaAccessToken: string): AxiosInstance => {
  const instance = axios.create();
  instance.interceptors.response.use(interceptBackgroundTask);
  instance.interceptors.response.use(
    response => {
      clearRegionalAuthError(region);
      return response;
    },
    async (error: unknown) => {
      if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) {
        throw error;
      }
      const failed = error.config as RetriableConfig;
      if (failed._usStaffTokenRetried) {
        const reminted = new RegionalApiError({
          kind: 'unauthorized',
          status: 401,
          reminted: true,
          message: 'US API rejected the replacement staff token (401)',
        });
        reportRegionalAuthError(region, reminted);
        throw reminted;
      }
      invalidateStaffRegionAccessToken(String(failed.headers?.Authorization ?? '').replace(/^Bearer /, ''));
      let token: string;
      try {
        token = await getUsStaffAccessToken(canadaAccessToken);
      } catch (mintError) {
        reportRegionalAuthError(region, mintError);
        throw mintError;
      }
      const retry: RetriableConfig = {
        ...failed,
        _usStaffTokenRetried: true,
        headers: { ...failed.headers, Authorization: `Bearer ${token}` },
      };
      return instance.request(retry);
    },
  );
  return instance;
};

export type RegionalHttp = {
  region: ApiRegion;
  /** Host without the `/api` suffix, e.g. `https://api.rxfoodapp.com`. */
  apiUrl: string;
  axios: AxiosInstance;
  configuration: Configuration;
  /** Authenticated fetch of a path such as `/api/meal_photos/1/photo` on this region's host. */
  fetch: (path: string, opts?: RegionalRequestOpts) => Promise<Response>;
};

/** US (or any non-Canada) host, token minting and 401 retry. Canada keeps the shared client. */
export const createRegionalHttp = (region: ApiRegion, canadaAccessToken: string): RegionalHttp => {
  const apiUrl = config.getApiUrlForRegion(region);
  const configuration = new Configuration({
    basePath: `${apiUrl}/api`,
    accessToken: async () => {
      try {
        return await getUsStaffAccessToken(canadaAccessToken);
      } catch (error) {
        reportRegionalAuthError(region, error);
        throw error;
      }
    },
  });
  const instance = createRegionalAxios(region, canadaAccessToken);
  return {
    region,
    apiUrl,
    axios: instance,
    configuration,
    fetch: (path, opts) => regionalRequest(canadaAccessToken, region, path, opts),
  };
};
